import { keyBy } from 'lodash';
import { Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { MutationResult } from 'apollo-angular';
import { Observable } from 'rxjs';
import { ApolloQueryResult } from '@apollo/client/core/types';

export interface EntitiesStateModel<E> {
  ids: string[];
  entityMap: Record<string, E>;
}

export function makeDefaultEntitiesState<E>(): EntitiesStateModel<E> {
  return {
    ids: [],
    entityMap: {},
  };
}

export function makeBaseEntitiesState<
  Entity extends {
    _id: string;
    __typename?: string;
  },
  StateModel extends EntitiesStateModel<Entity> = EntitiesStateModel<Entity>,
>(stateName: string) {
  @State({
    name: stateName,
    defaults: makeDefaultEntitiesState(),
  })
  @Injectable()
  class BaseEntitiesState {
    @Selector()
    static entityMap(state: { entityMap: Record<string, Entity> }): Record<string, Entity> {
      return state.entityMap;
    }

    @Selector()
    static entities(state: { entityMap: Record<string, Entity> }): Entity[] {
      return Object.values(state.entityMap);
    }

    protected patchEntitiesFromQueryResult<TData>(dataKey: string, ctx: StateContext<any>) {
      return (queryResult$: Observable<ApolloQueryResult<TData>>) => {
        return queryResult$.pipe(
          tap((result) => {
            if (result.errors) {
              throw new Error(result.errors.toString());
            }

            this.patchEntities(ctx, (result.data as any)?.[dataKey] as Entity[]);
          })
        );
      };
    }

    protected patchEntityFromQueryResult<TData, TDataKey = keyof Omit<TData, '__typename'>>(dataKey: TDataKey, ctx: StateContext<any>) {
      return (queryResult$: Observable<ApolloQueryResult<TData>>) => {
        return queryResult$.pipe(
          tap((result) => {
            if (result.errors) {
              throw new Error(result.errors.toString());
            }

            const entity = (result.data as any)?.[dataKey];

            if (!entity) {
              throw new Error(`Entity ${stateName} not found in query`);
            }

            this.patchEntity(ctx, entity as Entity);
          })
        );
      };
    }

    protected patchEntityFromMutationResult<TData, TDataKey = keyof Omit<TData, '__typename'>>(dataKey: TDataKey, ctx: StateContext<any>) {
      return (mutationResult$: Observable<MutationResult<TData>>) => {
        return mutationResult$.pipe(
          tap((result) => {
            if (result.errors) {
              throw new Error(result.errors.toString());
            }

            const entity = (result.data as any)?.[dataKey];

            if (!entity) {
              throw new Error(`Entity ${stateName} not found in mutation`);
            }

            this.patchEntity(ctx, entity as Entity);
          })
        );
      };
    }

    protected patchEntities(ctx: StateContext<StateModel>, entities: Entity[]): void {
      const state = ctx.getState();
      const ids = state.ids;
      const entityMap = state.entityMap;
      const newIds = entities.map((a) => a._id);
      const newEntityMap = keyBy(entities, '_id');
      ctx.patchState({
        ...state,
        ids: Array.from(new Set([...ids, ...newIds])),
        entityMap: { ...entityMap, ...newEntityMap } as { [_id: string]: Entity },
      });
    }

    protected patchEntity(ctx: StateContext<StateModel>, entity: Entity): void {
      const state = ctx.getState();
      const { entityMap, ids } = state;

      ctx.patchState({
        ...state,
        ids: Array.from(new Set([...ids, entity._id])),
        entityMap: { ...entityMap, [entity._id]: entity },
      });
    }
  }

  return BaseEntitiesState;
}
